import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIconButton from './DeleteIconButton';
import {
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  tableCellClasses,
  tableRowClasses,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import RemoteAutocomplete from './RemoteAutocomplete';
import { DEPARTURE_KEY_HELP, DEPARTURE_KEY_LABEL } from '../utils/tagCategories';

// The Category and Actions columns are frozen (position: sticky) and the checkbox columns
// scroll underneath them, so both cells must always paint an opaque background. They take it
// from this custom property, which the row classes set — including a pre-blended hover shade,
// because MUI's row hover is a translucent rgba(0, 0, 0, 0.04) overlay and a frozen cell that
// inherited it let the checkboxes hidden behind it show through (CP-7382).
const FROZEN_CELL_BG = '--frozen-cell-bg';
const TRIP_ROW_BG = 'white';
const TRIP_ROW_HOVER_BG = '#f5f5f5';
const INHERITED_ROW_BG = '#f5f5f5';
const INHERITED_ROW_HOVER_BG = '#ebebeb';

// MUI's own stickyHeader rule (position: sticky, z-index 2, injected after this sheet) ties a
// frozen header cell with every other header cell, so whichever comes later in the row paints
// over it once the table scrolls horizontally (CP-7382). Matching that class outranks it.
const STICKY_HEADER = `&.${tableCellClasses.stickyHeader}`;
const ROW_HOVER = `&.${tableRowClasses.hover}:hover`;

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: 600,
    '& .MuiTableCell-root': {
      padding: '4px 8px',
      fontSize: '0.75rem',
    },
  },
  headerCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    backgroundColor: '#f5f5f5',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  rotatedHeaderCell: {
    fontWeight: 600,
    backgroundColor: '#f5f5f5',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    height: 100,
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    padding: '4px 0 !important',
    verticalAlign: 'bottom',
    '& > span': {
      display: 'block',
      transform: 'rotate(-90deg)',
      whiteSpace: 'nowrap',
      transformOrigin: 'center center',
      width: 24,
      marginLeft: 8,
      marginBottom: 8,
    },
  },
  stickyColumn: {
    position: 'sticky',
    left: 0,
    backgroundColor: `var(${FROZEN_CELL_BG})`,
    zIndex: 2,
    minWidth: 200,
    borderRight: '1px solid #e0e0e0',
  },
  stickyHeaderColumn: {
    position: 'sticky',
    left: 0,
    minWidth: 200,
    borderRight: '1px solid #e0e0e0',
    [STICKY_HEADER]: {
      backgroundColor: '#f5f5f5',
      zIndex: 3,
    },
  },
  operatorDefaultRow: {
    backgroundColor: INHERITED_ROW_BG,
    [FROZEN_CELL_BG]: INHERITED_ROW_BG,
    [ROW_HOVER]: {
      [FROZEN_CELL_BG]: INHERITED_ROW_HOVER_BG,
    },
  },
  tripOverrideRow: {
    backgroundColor: TRIP_ROW_BG,
    [FROZEN_CELL_BG]: TRIP_ROW_BG,
    [ROW_HOVER]: {
      [FROZEN_CELL_BG]: TRIP_ROW_HOVER_BG,
    },
  },
  checkboxCell: {
    textAlign: 'center',
  },
  sourceChip: {
    marginLeft: '0.5rem',
    height: '18px',
    fontSize: '0.65rem',
  },
  actionCell: {
    position: 'sticky',
    right: 0,
    backgroundColor: `var(${FROZEN_CELL_BG})`,
    borderLeft: '1px solid #e0e0e0',
    // Without a z-index this cell only outranks the checkbox scrolled beneath it (a
    // position: relative ButtonBase) by tree order, and Rea's browser gave that checkbox
    // the pointer: its tooltip opened and the delete button never fired (CP-7382). 1 keeps
    // it above the checkboxes and below the sticky header row (2) it slides under.
    zIndex: 1,
  },
  actionHeaderCell: {
    position: 'sticky',
    right: 0,
    borderLeft: '1px solid #e0e0e0',
    [STICKY_HEADER]: {
      backgroundColor: '#f5f5f5',
      zIndex: 3,
    },
  },
}));

// Column definitions for feature flags.
// Exported so a test can assert every settings field in utils/tagCategories.js has a
// column: a field in the identity check but absent from the grid is invisible to the
// user while still deciding override-vs-inherited (CP-7983).
export const featureFlagColumns = [
  { id: 'required', label: 'Required', tooltip: 'This tag category is required', getValue: (cat) => cat.min_tags > 0 },
  { id: 'guides_from_tags', label: 'Guides', tooltip: 'Tags in this category can be linked to guides/staff (location sharing + chat)' },
  { id: 'chat_conversation', label: 'Chat Group', tooltip: 'Tags in this category create new chat conversations' },
  { id: 'visible_in_chat', label: 'Chat Visible', tooltip: 'Tags in this category should visually appear in chat' },
  { id: 'location_sharing', label: 'Loc Share', tooltip: 'Tags in this category determine location sharing groups' },
  { id: 'location_all_if_no_tag', label: 'Loc All', tooltip: "If a user doesn't have a tag from this category, they will see everyone (in this category)" },
  { id: 'user_association', label: 'GPS', tooltip: 'Tags in this category can be linked to GPS devices/users (location sharing, without chat)' },
  { id: 'user_selected', label: 'User Select', tooltip: 'Tags in this category can be self-selected by users on the self-registration page' },
  { id: 'max_tags', label: 'Single Tag', tooltip: 'Only one tag allowed of this category per user, per departure', getValue: (cat) => cat.max_tags === 1 },
  { id: 'allow_multiple_tags', label: 'Multi Tags', tooltip: 'Allow more than one tag in this category', getValue: (cat) => cat.max_tags > 1 },
  { id: 'remove_upon_date_changes', label: 'Rm on Date', tooltip: 'Remove this tag if date changes via XML/integration' },
  { id: 'checkin', label: 'Check-in', tooltip: 'Tags in this category are used for check-in' },
  { id: 'boarding', label: 'Boarding', tooltip: 'Tags in this category are used for boarding' },
  { id: 'cleanup_on_assignment', label: 'Cleanup', tooltip: 'Cleanup on assignment' },
  { id: 'departure_wide', label: 'Dept Wide', tooltip: 'Departure wide assignment' },
  { id: 'remove_guides_on_cleanup', label: 'Rm Guides', tooltip: 'Remove guides on cleanup' },
  { id: 'remove_unreferenced_guides', label: 'Rm Unref Guides', tooltip: 'Remove unreferenced guides' },
  { id: 'copy_tags_to_guides', label: 'Copy Guides', tooltip: 'Copy user tags to guides' },
  { id: 'copy_tags_to_subbookings', label: 'Copy Subbkg', tooltip: 'Copy tags to sub-bookings' },
  { id: 'checkin_display', label: 'Chk Display', tooltip: 'Display tags during check-in' },
  // Part of the settings identity check (utils/tagCategories.js) but for a long time it had
  // no column, so a trip row differing from its operator default ONLY in this flag looked
  // byte-identical to the default while behaving as a genuine override (CP-7983).
  { id: 'visible_on_bookingmgmt', label: 'Bkg Mgmt', tooltip: 'Tags in this category are visible on the guest booking-management page' },
];

// Trip Defaults (operator mode) adds the operator-only flags (utils/tagCategories.js
// OPERATOR_DEFAULT_ONLY_BOOL_SETTINGS). Departure identity sits right after Dept Wide, as in
// the console's Lifecycle group (cleanup, departure-wide, departure identity, remove on date change).
export const operatorDefaultFlagColumns = featureFlagColumns.flatMap((col) => (
  col.id === 'departure_wide'
    ? [col, { id: 'departure_key', label: DEPARTURE_KEY_LABEL, tooltip: DEPARTURE_KEY_HELP }]
    : [col]
));

// Sub-component for category name cell with optional dropdown
const CategoryNameCell = React.memo(({
  cat,
  showDropdown,
  isReadOnly,
  hasWritePerm,
  apiFilters,
  isParentTouched,
  onAssociatedTagCategoryChange,
  className,
}) => {
  const [isTouched, setIsTouched] = useState(false);

  const handleChange = (ev, selectedCategory) => {
    setIsTouched(true);
    onAssociatedTagCategoryChange(ev, selectedCategory, cat.idx);
  };

  return (
    <TableCell className={className}>
      {showDropdown ? (
        <RemoteAutocomplete
          resource="tour_tag_category"
          label="Tag Category"
          variant="filled"
          size="small"
          disabled={!hasWritePerm}
          disableClearable={true}
          onChange={handleChange}
          value={cat.category}
          getOptionLabel={(opt) => opt.name}
          apiFilters={apiFilters}
          textFieldProps={{
            onBlur: () => setIsTouched(true),
            error: (isParentTouched || isTouched) && !cat.category?.id,
            helperText: (isParentTouched || isTouched) && !cat.category?.id ? 'Select a category' : undefined,
            sx: { minWidth: 180 }
          }}
        />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {cat.category?.name || 'Unknown Category'}
        </Typography>
      )}
    </TableCell>
  );
});

CategoryNameCell.displayName = 'CategoryNameCell';

const TagCategoryTableView = ({
  categories,
  mode,
  hasWritePerm,
  hasUpdateTagCategoriesPerm,
  tripObject,
  isParentTouched,
  checkinCategoriesCount = 0,
  boardingCategoriesCount = 0,
  onAssociatedTagCategoryChange,
  onAssociatedCategoryCheckboxChange,
  onCheckinBoardingCheckboxChange,
  onAssociatedCategoryDelete,
  onCreateOverride,
}) => {
  const classes = useStyles();
  const canManageCategories = hasWritePerm && (mode === 'operator' || hasUpdateTagCategoriesPerm);
  const flagColumns = mode === 'operator' ? operatorDefaultFlagColumns : featureFlagColumns;

  if (categories.length === 0) {
    return (
      <Typography style={{ fontSize: '90%', marginBottom: '1rem', paddingLeft: '1rem' }}>
        No tag categories found.
      </Typography>
    );
  }

  const handleCheckboxChange = (cat, field) => (ev) => {
    // Create a synthetic event for the handler
    const syntheticEvent = {
      ...ev,
      target: {
        ...ev.target,
        checked: ev.target.checked,
      },
      stopPropagation: () => {},
    };

    // Map special fields
    let actualField = field;
    if (field === 'required') {
      actualField = 'required';
    }

    // checkin and boarding use a special handler
    if (field === 'checkin' || field === 'boarding') {
      onCheckinBoardingCheckboxChange(syntheticEvent, cat.idx, cat.id, field);
    } else {
      onAssociatedCategoryCheckboxChange(syntheticEvent, cat.idx, actualField);
    }
  };

  const compareFn = (a, b) => {
      const nameA = (a.category?.name || '').toLowerCase();
      const nameB = (b.category?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell className={`${classes.headerCell} ${classes.stickyHeaderColumn}`}>
              Category
            </TableCell>
            {(mode === 'trip' || mode === 'create') && (
              <TableCell className={classes.headerCell}>Source</TableCell>
            )}
            {flagColumns.map(col => (
              <TableCell key={col.id}  align="center">
                <Tooltip title={col.tooltip}>
                  <span>{col.label}</span>
                </Tooltip>
              </TableCell>
            ))}
            <TableCell className={`${classes.headerCell} ${classes.actionHeaderCell}`} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categories.toSorted(compareFn).map((cat, index) => {
            const isOperatorDefault = cat.source === 'operator';
            // In trip or create mode, all operator default categories are read-only (inherited)
            const isReadOnly = (mode === 'trip' || mode === 'create') && isOperatorDefault;
            const rowClassName = isReadOnly ? classes.operatorDefaultRow : classes.tripOverrideRow;

            // Build API filters for category selection - only exclude trip-level categories, not operator defaults
            const tripLevelCategories = categories.filter(c => c.source === 'trip');
            const allUsedIds = [...new Set(tripLevelCategories
              .filter(c => c.category?.id)
              .map(c => c.category.id.toString()))];
            const currentCategoryId = cat.category?.id?.toString();
            const excludeIds = currentCategoryId ? allUsedIds.filter(id => id !== currentCategoryId) : allUsedIds;

            const apiFilters = [];
            if (excludeIds.length > 0) {
              apiFilters.push({ field: 'id', op: 'not_in', value: excludeIds });
            }
            if (tripObject?.is_bespoke) {
              apiFilters.push({ field: 'code', op: 'ne', value: 'departure' });
            }

            const showCategoryDropdown = cat.is_new && !cat.category?.id && !isReadOnly;

            return (
              <TableRow
                key={cat.idx || cat.id || cat.category?.id || index}
                className={rowClassName}
                hover
              >
                <CategoryNameCell
                  cat={cat}
                  showDropdown={showCategoryDropdown}
                  isReadOnly={isReadOnly}
                  hasWritePerm={canManageCategories}
                  apiFilters={apiFilters}
                  isParentTouched={isParentTouched}
                  onAssociatedTagCategoryChange={onAssociatedTagCategoryChange}
                  className={classes.stickyColumn}
                />

                {(mode === 'trip' || mode === 'create') && (
                  <TableCell>
                    <Chip
                      label={
                        isOperatorDefault
                          ? 'Inherited'
                          : (cat.isOverride ? 'Override' : 'Trip')
                      }
                      size="small"
                      color={isOperatorDefault ? 'warning' : 'primary'}
                      variant={isOperatorDefault ? 'filled' : 'filled'}
                      className={classes.sourceChip}
                    />
                  </TableCell>
                )}

                {flagColumns.map(col => {
                  const value = col.getValue ? col.getValue(cat) : !!cat[col.id];
                  // Allow editing if category is new (and selected) OR user has update_tag_categories permission
                  // Don't allow editing checkboxes until a category is selected
                  const hasCategorySelected = !!cat.category?.id;
                  const canEditFeatureFlags = hasUpdateTagCategoriesPerm && hasCategorySelected;
                  const isDisabled = isReadOnly || !canEditFeatureFlags || !hasCategorySelected;

                  // Special disabling logic for certain fields
                  let extraDisabled = false;
                  if (col.id === 'required' && cat.category?.code === 'departure') {
                    extraDisabled = true;
                  }
                  if (col.id === 'user_association' && !cat.location_sharing) {
                    extraDisabled = true;
                  }
                  if (col.id === 'remove_guides_on_cleanup' && !cat.cleanup_on_assignment) {
                    extraDisabled = true;
                  }
                  if (col.id === 'remove_unreferenced_guides' && !cat.guides_from_tags) {
                    extraDisabled = true;
                  }
                  if (col.id === 'max_tags' && cat.max_tags > 1) {
                    extraDisabled = true;
                  }
                  if (col.id === 'allow_multiple_tags' && cat.max_tags === 1) {
                    extraDisabled = true;
                  }
                  // Check-in / boarding: several rules can block the checkbox, and each one
                  // explains itself via the tooltip — otherwise a greyed-out box in an
                  // unrelated row looks like a bug.
                  // Mutual exclusivity: a category is either a check-in or a boarding one,
                  // never both. Only the unchecked flag is disabled, so legacy rows that
                  // already have both can still be fixed by unchecking either of them.
                  let disabledHint = null;
                  if (col.id === 'checkin' && !cat.checkin) {
                    if (cat.boarding) {
                      extraDisabled = true;
                      disabledHint = 'This category is already used for boarding — uncheck Boarding first';
                    } else if (checkinCategoriesCount >= 3) {
                      extraDisabled = true;
                      disabledHint = 'There are already 3 check-in tag categories selected';
                    }
                  }
                  if (col.id === 'boarding' && !cat.boarding) {
                    if (cat.checkin) {
                      extraDisabled = true;
                      disabledHint = 'This category is already used for check-in — uncheck Check-in first';
                    } else if (boardingCategoriesCount >= 1) {
                      extraDisabled = true;
                      disabledHint = 'There is already 1 boarding tag category selected (on another category)';
                    }
                  }

                  const finalDisabled = isDisabled || extraDisabled;

                  return (
                    <TableCell key={col.id} className={classes.checkboxCell} data-flag={col.id}>
                      <Tooltip title={disabledHint || col.tooltip} placement="top">
                        <span>
                          <Checkbox
                            size="small"
                            checked={value}
                            disabled={finalDisabled}
                            onChange={finalDisabled ? undefined : handleCheckboxChange(cat, col.id)}
                            sx={{ padding: '2px' }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  );
                })}

                <TableCell className={classes.actionCell} align="center">
                  {isReadOnly ? (
                    <Tooltip title="Create trip-level override">
                      <IconButton
                        size="small"
                        onClick={() => onCreateOverride(cat)}
                        disabled={!canManageCategories}
                      >
                        <ContentCopyIcon fontSize="small" color={canManageCategories ? 'primary' : 'disabled'} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <DeleteIconButton
                      allowed={canManageCategories}
                      onClick={() => onAssociatedCategoryDelete(cat.idx)}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

TagCategoryTableView.displayName = 'TagCategoryTableView';

TagCategoryTableView.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    category: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      code: PropTypes.string,
    }),
    source: PropTypes.oneOf(['operator', 'trip']),
    isOverride: PropTypes.bool,
    isInTrip: PropTypes.bool,
    is_new: PropTypes.bool,
    min_tags: PropTypes.number,
    max_tags: PropTypes.number,
    guides_from_tags: PropTypes.bool,
    chat_conversation: PropTypes.bool,
    visible_in_chat: PropTypes.bool,
    location_sharing: PropTypes.bool,
    location_all_if_no_tag: PropTypes.bool,
    user_association: PropTypes.bool,
    user_selected: PropTypes.bool,
    remove_upon_date_changes: PropTypes.bool,
    checkin: PropTypes.bool,
    boarding: PropTypes.bool,
    cleanup_on_assignment: PropTypes.bool,
    departure_wide: PropTypes.bool,
    departure_key: PropTypes.bool,
    remove_guides_on_cleanup: PropTypes.bool,
    remove_unreferenced_guides: PropTypes.bool,
    copy_tags_to_guides: PropTypes.bool,
    copy_tags_to_subbookings: PropTypes.bool,
    checkin_display: PropTypes.bool,
  })).isRequired,
  mode: PropTypes.oneOf(['operator', 'trip', 'create']).isRequired,
  hasWritePerm: PropTypes.bool,
  hasUpdateTagCategoriesPerm: PropTypes.bool,
  tripObject: PropTypes.object,
  isParentTouched: PropTypes.any,
  checkinCategoriesCount: PropTypes.number,
  boardingCategoriesCount: PropTypes.number,
  onAssociatedTagCategoryChange: PropTypes.func,
  onAssociatedCategoryCheckboxChange: PropTypes.func.isRequired,
  onCheckinBoardingCheckboxChange: PropTypes.func.isRequired,
  onAssociatedCategoryDelete: PropTypes.func.isRequired,
  onCreateOverride: PropTypes.func.isRequired,
};

CategoryNameCell.propTypes = {
  cat: PropTypes.object.isRequired,
  showDropdown: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  hasWritePerm: PropTypes.bool,
  apiFilters: PropTypes.array,
  isParentTouched: PropTypes.any,
  onAssociatedTagCategoryChange: PropTypes.func,
  className: PropTypes.string,
};

export default TagCategoryTableView;
